import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import ArticlePage from "./pages/ArticlePage";
import ArticleEditPage from "./pages/ArticleEditPage";
import CategoryPage from "./pages/CategoryPage";
import DestinationPage from "./pages/DestinationPage";
import SearchPage from "./pages/SearchPage";
import DealsPage from "./pages/DealsPage";
import AboutPage from "./pages/AboutPage";
import JoinPage from "./pages/JoinPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFoundPage from "./pages/NotFoundPage";
import RegisterPage from "./pages/RegisterPage";
import MemberPage from "./pages/MemberPage";
import DashboardPage from "./pages/DashboardPage";
import ArticlePreviewPage from "./pages/ArticlePreviewPage";
import AIEditorPage from "./pages/AIEditorPage";
import ImportDraftPage from "./pages/ImportDraftPage";
// BatchPhotoImportPage: fichier conservé pour audit ultérieur, route retirée pour MEP P0
// import BatchPhotoImportPage from "./pages/BatchPhotoImportPage";
import PartnerRequestPage from "./pages/PartnerRequestPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import { ProductReviewsListPage, ProductReviewDetailPage } from "./pages/ProductReviewsPage";
import MobileUploadPage from "./pages/MobileUploadPage";
import LegalPage from "./pages/LegalPage";
import PrivacyPage from "./pages/PrivacyPage";
import TripListPage from "./pages/TripListPage";
import TripPage from "./pages/TripPage";
import TripDayPage from "./pages/TripDayPage";
import VerifyMemberPage from "./pages/VerifyMemberPage";
import AdhesionPage from "./pages/AdhesionPage";
import AdhesionReturnPage from "./pages/AdhesionReturnPage";
import PrivilegesListPage from "./pages/PrivilegesListPage";
import PrivilegeDetailPage from "./pages/PrivilegeDetailPage";
import { useParams } from "react-router-dom";
import { PreLaunchGuard, useSiteStatus } from "./components/PreLaunchGuard";
import PreLaunchLegalLayout from "./components/PreLaunchLegalLayout";
import BlueBridge from "./modules/blue-chat-bridge/BlueBridge";
import CookieBanner from "./components/CookieBanner";
import NewsletterConfirmPage from "./pages/NewsletterConfirmPage";
import NewsletterUnsubscribePage from "./pages/NewsletterUnsubscribePage";
import DonationsPage from "./pages/DonationsPage";
import PartnerPortalPage from "./pages/PartnerPortalPage";
import ReporterPage from "./pages/ReporterPage";
import CharteEditorialePage from "./pages/CharteEditorialePage";
import LaRedactionPage from "./pages/LaRedactionPage";
import PracticalSheetPage from "./pages/PracticalSheetPage";
import SeoLang from "./components/SeoLang";
import i18nInstance, { SUPPORTED_CODES } from "./i18n";
import "@/App.css";
import React, { useEffect } from "react";

// Redirects legacy /voyage/... => /carnets-de-voyage/... (Bloc 1)
function VoyageSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/carnets-de-voyage/${slug}`} replace />;
}
function VoyageDayRedirect() {
  const { tripSlug, daySlug } = useParams();
  return <Navigate to={`/carnets-de-voyage/${tripSlug}/journee/${daySlug}`} replace />;
}

// MODE PRÉ-LANCEMENT: piloté runtime via GET /api/public/site-status
// (voir components/PreLaunchGuard.js). Plus de constante hardcodée.

// ErrorBoundary pour capturer les erreurs React et éviter les écrans blancs
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-secondary p-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-6">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Une erreur est survenue</h1>
            <p className="text-gray-600 mb-4">
              L'application a rencontré un problème. Vos données ne sont pas perdues.
            </p>
            <details className="mb-4 p-3 bg-gray-50 rounded text-sm">
              <summary className="cursor-pointer font-medium">Détails de l'erreur</summary>
              <pre className="mt-2 overflow-auto text-xs text-red-700 whitespace-pre-wrap">
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Recharger la page
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Composant de protection pré-lancement — délègue à PreLaunchGuard qui interroge le backend
function PreLaunchRoute({ children }) {
  const { loading } = useAuth();

  // Attendre le chargement de l'auth avant de laisser PreLaunchGuard décider
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return <PreLaunchGuard>{children}</PreLaunchGuard>;
}

// Phase 14 STEP 1 — chemins légaux/consentement/auth accessibles à un visiteur
// NON authentifié en mode pré-lancement. Ils ne doivent JAMAIS exposer la
// navigation publique normale (menus, recherche, découverte d'articles).
const MINIMAL_CHROME_PREFIXES = [
  "/mentions-legales",
  "/confidentialite",
  "/connexion",
  "/mot-de-passe-oublie",
  "/reinitialiser-mot-de-passe",
  "/newsletter/confirmer",
  "/newsletter/desinscription",
];
// Sous-ensemble « pages de contenu légal » qui reçoit la coquille dédiée
// (logo + retour). Les pages d'auth ont déjà leur propre plein écran centré.
const LEGAL_LAYOUT_PREFIXES = [
  "/mentions-legales",
  "/confidentialite",
  "/newsletter/confirmer",
  "/newsletter/desinscription",
];

function matchesPrefix(pathname, prefixes) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p));
}

// Enveloppe une page légale : en pré-lancement + non authentifié → coquille
// minimale ; sinon rendu normal (la navbar/footer publics sont gérés par AppContent).
function LegalChrome({ children }) {
  const preLaunch = useSiteStatus();
  const { isAuthenticated } = useAuth();
  if (preLaunch && !isAuthenticated) {
    return <PreLaunchLegalLayout>{children}</PreLaunchLegalLayout>;
  }
  return children;
}

// Phase finale — vraies routes préfixées par langue (/fr /en /es /it /zh-cn /ja).
// Entrée localisée non destructive : fixe la langue puis redirige vers la route
// non préfixée équivalente (le contenu localisé est servi via l'état i18n).
// Les URLs existantes non préfixées restent valides (fallback FR). Aucun SSR.
function LangEntry() {
  const params = useParams();
  const location = useLocation();
  const code = (params.lng || "").toLowerCase();
  const supported = SUPPORTED_CODES.includes(code);
  useEffect(() => {
    if (supported) i18nInstance.changeLanguage(code);
  }, [code, supported]);
  if (!supported) return <NotFoundPage />;
  const rest = params["*"] || "";
  return <Navigate to={`/${rest}${location.search || ""}`} replace />;
}

function AppContent() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const preLaunch = useSiteStatus();

  // En mode pré-lancement, masquer navbar/footer sur la page coming-soon.
  // /dashboard/* rend sa propre coquille admin (sidebar) — pas de navbar/footer publics.
  const isComingSoonPage = location.pathname === "/coming-soon";
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  const isReporterRoute = location.pathname.startsWith("/reporter");
  // Phase 14 STEP 1 — verrou pré-lancement : sur les pages légales/auth/consentement,
  // un visiteur NON authentifié ne doit voir NI la navbar ni le footer publics
  // (sinon fuite de la navigation ITERCATUS derrière Coming Soon).
  const hidePublicChrome =
    preLaunch && !isAuthenticated && matchesPrefix(location.pathname, MINIMAL_CHROME_PREFIXES);
  const showNavFooter =
    !isComingSoonPage && !isDashboardRoute && !isReporterRoute && !hidePublicChrome;

  // La vitrine Coming Soon est publique et indexable, même en pré-lancement.
  // Conserver le noindex pré-lancement sur les autres routes (contenus non finis).
  // Cette règle ne modifie que la balise créée ici, pas celles des aperçus privés.
  useEffect(() => {
    const id = "prelaunch-robots-meta";
    let tag = document.querySelector(`meta#${id}`);
    if (preLaunch && !isComingSoonPage) {
      if (!tag) {
        tag = document.createElement("meta");
        tag.id = id;
        tag.name = "robots";
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", "noindex, nofollow");
    } else if (tag) {
      tag.remove();
    }
  }, [preLaunch, isComingSoonPage]);

  return (
    <div className="min-h-screen flex flex-col bg-secondary">
      <SeoLang />
      {showNavFooter && <Navbar />}
      <main className="flex-1">
        <Routes>
          {/* Page Coming Soon - toujours accessible */}
          <Route path="/coming-soon" element={<ComingSoonPage />} />
          
          {/* Routes publiques même en pré-lancement */}
          <Route path="/connexion" element={<LoginPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPasswordPage />} />
          <Route path="/mentions-legales" element={<LegalChrome><LegalPage /></LegalChrome>} />
          <Route path="/confidentialite" element={<LegalChrome><PrivacyPage /></LegalChrome>} />
          <Route path="/entreprises" element={<PreLaunchRoute><PartnerRequestPage /></PreLaunchRoute>} />
          <Route path="/partenaires" element={<PreLaunchRoute><PartnerRequestPage /></PreLaunchRoute>} />
          <Route path="/verify-member/:token" element={<PreLaunchRoute><VerifyMemberPage /></PreLaunchRoute>} />
          <Route path="/adhesion" element={<PreLaunchRoute><AdhesionPage /></PreLaunchRoute>} />
          <Route path="/adhesion/retour" element={<PreLaunchRoute><AdhesionReturnPage /></PreLaunchRoute>} />
          <Route path="/newsletter/confirmer/:token" element={<LegalChrome><NewsletterConfirmPage /></LegalChrome>} />
          <Route path="/newsletter/desinscription/:token" element={<LegalChrome><NewsletterUnsubscribePage /></LegalChrome>} />
          <Route path="/soutenir" element={<PreLaunchRoute><DonationsPage /></PreLaunchRoute>} />
          <Route path="/portail-partenaires" element={<PreLaunchRoute><PartnerPortalPage /></PreLaunchRoute>} />
          
          {/* Routes protégées par le mode pré-lancement */}
          <Route path="/" element={<PreLaunchRoute><HomePage /></PreLaunchRoute>} />
          <Route path="/article/:id" element={<PreLaunchRoute><ArticlePage /></PreLaunchRoute>} />
          <Route path="/article/:id/edit" element={<PreLaunchRoute><ArticleEditPage /></PreLaunchRoute>} />
          <Route path="/categorie/:slug" element={<PreLaunchRoute><CategoryPage /></PreLaunchRoute>} />
          <Route path="/destination/:slug" element={<PreLaunchRoute><DestinationPage /></PreLaunchRoute>} />
          <Route path="/recherche" element={<PreLaunchRoute><SearchPage /></PreLaunchRoute>} />
          <Route path="/bons-plans" element={<PreLaunchRoute><DealsPage /></PreLaunchRoute>} />
          <Route path="/tests-produits" element={<PreLaunchRoute><ProductReviewsListPage /></PreLaunchRoute>} />
          <Route path="/tests-produits/:id" element={<PreLaunchRoute><ProductReviewDetailPage /></PreLaunchRoute>} />
          <Route path="/voyages" element={<Navigate to="/carnets-de-voyage" replace />} />
          <Route path="/voyage/:slug" element={<VoyageSlugRedirect />} />
          <Route path="/voyage/:tripSlug/journee/:daySlug" element={<VoyageDayRedirect />} />
          <Route path="/carnets-de-voyage" element={<PreLaunchRoute><TripListPage /></PreLaunchRoute>} />
          <Route path="/carnets-de-voyage/:slug" element={<PreLaunchRoute><TripPage /></PreLaunchRoute>} />
          <Route path="/carnets-de-voyage/:tripSlug/journee/:daySlug" element={<PreLaunchRoute><TripDayPage /></PreLaunchRoute>} />
          <Route path="/fiche-pratique/:slug" element={<PreLaunchRoute><PracticalSheetPage /></PreLaunchRoute>} />
          <Route path="/association" element={<PreLaunchRoute><AboutPage /></PreLaunchRoute>} />
          <Route path="/charte-editoriale" element={<PreLaunchRoute><CharteEditorialePage /></PreLaunchRoute>} />
          <Route path="/la-redaction" element={<PreLaunchRoute><LaRedactionPage /></PreLaunchRoute>} />
          <Route path="/adherer" element={<PreLaunchRoute><JoinPage /></PreLaunchRoute>} />
          <Route path="/privileges" element={<PreLaunchRoute><PrivilegesListPage /></PreLaunchRoute>} />
          <Route path="/privileges/:id" element={<PreLaunchRoute><PrivilegeDetailPage /></PreLaunchRoute>} />
          <Route path="/inscription" element={<PreLaunchRoute><RegisterPage /></PreLaunchRoute>} />
          <Route path="/espace-membre" element={<PreLaunchRoute><MemberPage /></PreLaunchRoute>} />
          <Route path="/upload" element={<PreLaunchRoute><MobileUploadPage /></PreLaunchRoute>} />
          <Route path="/edit/:type/:id" element={<PreLaunchRoute><AIEditorPage /></PreLaunchRoute>} />
          {/* MEP P0: /ai-editor (chat IA abandonné P1 septies) → redirect vers workflow import JSON */}
          <Route path="/ai-editor" element={<Navigate to="/import-draft" replace />} />
          {/* MEP P0: /batch-import masqué (outil interne non finalisé) */}
          <Route path="/import-draft" element={<PreLaunchRoute><ImportDraftPage /></PreLaunchRoute>} />
          <Route path="/intelligence/preview/:id" element={<ArticlePreviewPage />} />
          <Route path="/dashboard/*" element={<PreLaunchRoute><DashboardPage /></PreLaunchRoute>} />
          {/* Phase 9 — ITERCATUS Reporter (PWA, éditorial authentifié uniquement) */}
          <Route path="/reporter" element={<PreLaunchRoute><ReporterPage /></PreLaunchRoute>} />
          {/* Legacy public route → send everyone to the admin-shell rendering under /dashboard */}
          <Route path="/admin/blue-studio" element={<Navigate to="/dashboard/blue-studio" replace />} />

          {/* Vraies routes de langue — matchées seulement si le 1er segment est une
              langue supportée (les routes statiques ont priorité de matching). */}
          <Route path="/:lng/*" element={<LangEntry />} />

          {/* Catch-all: page 404 sobre en français, toujours accessible */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      {showNavFooter && <Footer />}
      {/* Blue - Mascotte & Guide (monte une seule fois, se masque automatiquement sur /dashboard/*).
          Masqué aussi sur les pages légales pré-lancement (pas d'assistant de découverte). */}
      {!isComingSoonPage && !hidePublicChrome && <BlueBridge />}
      {/* Bannière CMP cookies — VISIBLE aussi sur /coming-soon (page publique
          principale en mode pré-lancement). Tant qu'aucun consentement n'est
          stocké dans localStorage. */}
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;